import { useCallback, useContext } from 'react'
import * as React from 'react'
import moment from 'moment'
import { useIntl, FormattedMessage } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import LineRefundIcon from 'LuxKit/Icons/line/LineRefundIcon'
import TextLink from 'LuxKit/TextLink'
import { INTL_DAY_MONTH_NAME_SHORT } from 'constants/dateFormats'
import Clickable from 'components/Common/Clickable'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import styled from 'styled-components'
import ModalContext from 'contexts/ModalContext'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { getFreeCancellationStartDate } from 'lib/offer/cancellationPolicyUtils'
import OfferPropertyCancellationPolicyModal from './OfferPropertyCancellationPolicyModal'
import { useAppSelector } from 'hooks/reduxHooks'
import useRebrandSuccessDarkColour from 'hooks/useRebrandSuccessDarkColour'

const MiddledTooltipIcon = styled(LineInfoCircleIcon)`
  vertical-align: middle;
`

interface Props {
  propertyTimezoneOffset?: number
  checkInDate?: string | moment.Moment
  isFlashUSP?: boolean
  showHotelOnly?: boolean
}

function OfferPropertyCancellationPolicies(props: Props) {
  const { propertyTimezoneOffset, checkInDate, isFlashUSP, showHotelOnly = false } = props
  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()
  const purchaseDate = useAppSelector(state => state.checkout.cart.existingOrder?.createdAt)
  const startDate = getFreeCancellationStartDate(checkInDate, purchaseDate, propertyTimezoneOffset)
  const uspColour = useRebrandSuccessDarkColour()

  const showModal = useContext(ModalContext)

  const onItemClick = useCallback(() => {
    showModal(<OfferPropertyCancellationPolicyModal startDate={startDate} checkInDate={checkInDate} />)
  }, [showModal, startDate, checkInDate])

  const freeCancellationLabel = showHotelOnly ? intl.formatMessage({
    id: 'w1c3ScLBmgUZLx4bh8gk',
    defaultMessage: 'Free cancellation (Hotel only)',
    description: 'Cancellation link clarifying hotel-only coverage when another travel product is selected',
  }) : intl.formatMessage({
    id: 'qUOBB5iD-FihhOtgXUap',
    defaultMessage: 'Free cancellation',
    description: 'Bold label for free cancellation policy in offer property cancellation',
  })

  const highlightText = useCallback((chunks: React.ReactNode) => (
    <BodyText variant="medium" weight="bold" as="span" colour={uspColour}>{chunks}</BodyText>
  ), [uspColour])

  if (startDate?.isBefore(moment())) {
    return null
  }

  return <>
    {!isFlashUSP && <Clickable onClick={onItemClick} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <BodyText variant="medium" as="span">
        {!startDate && showHotelOnly && <FormattedMessage
          id="jQTenrMzd2R-OtCoE4FP"
          defaultMessage="<highlight>Free cancellation</highlight> within 7 days of purchase (Hotel only)"
          description="Free cancellation policy within 7 days of purchase for hotel bookings"
          values={{ highlight: highlightText }}
        />}
        {!startDate && !showHotelOnly && <FormattedMessage
          id="pHpH7Bp1b6HaZ37I3qzy"
          defaultMessage="<highlight>Free cancellation</highlight> within 7 days of purchase"
          description="Free cancellation policy within seven days of purchase for standalone hotel bookings"
          values={{ highlight: highlightText }}
        />}
        {startDate && showHotelOnly && <FormattedMessage
          id="c6nHzkcCViVea5jzyGZX"
          defaultMessage="<highlight>Free cancellation</highlight> until {date} (Hotel only)"
          description="Free cancellation policy with deadline date for hotel bookings"
          values={{
            highlight: highlightText,
            date: intlDateFormatter(startDate, INTL_DAY_MONTH_NAME_SHORT),
          }}
        />}
        {startDate && !showHotelOnly && <FormattedMessage
          id="2WFSE-9OntMCObRo3ovK"
          defaultMessage="<highlight>Free cancellation</highlight> until {date}"
          description="Free cancellation policy with deadline date for standalone hotel bookings"
          values={{
            highlight: highlightText,
            date: intlDateFormatter(startDate, INTL_DAY_MONTH_NAME_SHORT),
          }}
        />}
      </BodyText>
      <MiddledTooltipIcon size="XS" />
    </Clickable>}
    {isFlashUSP && <TextLink
      size="medium"
      colour={uspColour}
      underline={false}
      data-type="7day"
      onClick={onItemClick}
      startIcon={<LineRefundIcon />}
    >
      {freeCancellationLabel}
    </TextLink>}
  </>
}

export default OfferPropertyCancellationPolicies
